import {
  ADMIN_ACCOUNT_STATUSES,
  ADMIN_APP_ROLES,
  formatAppRoles,
  isAccountPaused,
} from "@/config/adminAccountOptions";
import type { AdminAccountRow } from "@/types/systemAdmin";

/** Sentinel used by the filter <select>s for "no filter applied". */
export const ALL_FILTER_VALUE = "__all__";

/** Sentinel for accounts that have no org role assigned. */
export const NO_ORG_ROLE_VALUE = "__none__";

/**
 * Single filter bucket covering every status `isAccountPaused` treats as paused
 * (`suspended`, `inactive`, `removed`), matching how the Status column renders them.
 */
export const PAUSED_STATUS_VALUE = "paused";

export type AccountFilterKey = "status" | "district" | "school" | "orgRole" | "admin";

/** An empty string means the filter is not applied. */
export type AccountFilters = Record<AccountFilterKey, string>;

export const EMPTY_ACCOUNT_FILTERS: AccountFilters = {
  status: "",
  district: "",
  school: "",
  orgRole: "",
  admin: "",
};

export const ACCOUNT_FILTER_LABELS: Record<AccountFilterKey, string> = {
  status: "Status",
  district: "District",
  school: "School",
  orgRole: "Org role",
  admin: "Admin",
};

export interface FilterOption {
  value: string;
  label: string;
}

export interface AccountFilterOptions {
  status: FilterOption[];
  district: FilterOption[];
  school: FilterOption[];
  orgRole: FilterOption[];
  admin: FilterOption[];
}

function statusFilterValue(row: AdminAccountRow): string {
  return isAccountPaused(row.userStatus) ? PAUSED_STATUS_VALUE : row.userStatus;
}

function statusFilterLabel(value: string): string {
  if (value === PAUSED_STATUS_VALUE) return "Paused";
  return ADMIN_ACCOUNT_STATUSES.find((option) => option.value === value)?.label ?? value;
}

function byLabel(a: FilterOption, b: FilterOption): number {
  return a.label.localeCompare(b.label, "en");
}

function buildStatusOptions(accounts: AdminAccountRow[]): FilterOption[] {
  const values = new Set(accounts.map(statusFilterValue));
  return [...values].map((value) => ({ value, label: statusFilterLabel(value) })).sort(byLabel);
}

function buildDistrictOptions(accounts: AdminAccountRow[]): FilterOption[] {
  const namesById = new Map<string, string>();
  for (const row of accounts) {
    if (row.districtId && row.districtName) namesById.set(row.districtId, row.districtName);
  }
  return [...namesById].map(([value, label]) => ({ value, label })).sort(byLabel);
}

export function buildSchoolOptions(
  accounts: AdminAccountRow[],
  districtId: string
): FilterOption[] {
  const namesById = new Map<string, string>();
  for (const row of accounts) {
    if (!row.schoolId || !row.schoolName) continue;
    if (districtId && row.districtId !== districtId) continue;
    namesById.set(row.schoolId, row.schoolName);
  }
  return [...namesById].map(([value, label]) => ({ value, label })).sort(byLabel);
}

function buildOrgRoleOptions(accounts: AdminAccountRow[]): FilterOption[] {
  const values = new Set<string>();
  let hasUnassigned = false;
  for (const row of accounts) {
    if (row.roles.length === 0) {
      hasUnassigned = true;
      continue;
    }
    for (const role of row.roles) values.add(role);
  }

  const options = [...values]
    .map((value) => ({
      value,
      label: ADMIN_APP_ROLES.find((option) => option.value === value)?.label ?? value,
    }))
    .sort(byLabel);

  return hasUnassigned ? [...options, { value: NO_ORG_ROLE_VALUE, label: "No org role" }] : options;
}

export function buildAccountFilterOptions(
  accounts: AdminAccountRow[],
  filters: AccountFilters
): AccountFilterOptions {
  return {
    status: buildStatusOptions(accounts),
    district: buildDistrictOptions(accounts),
    school: buildSchoolOptions(accounts, filters.district),
    orgRole: buildOrgRoleOptions(accounts),
    admin: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  };
}

/**
 * Selecting a district can strand a school filter that belongs to another district,
 * which would render an empty list with no obvious cause. Drop it when that happens.
 */
export function applyDistrictFilter(
  filters: AccountFilters,
  districtId: string,
  accounts: AdminAccountRow[]
): AccountFilters {
  const schoolStillAvailable = buildSchoolOptions(accounts, districtId).some(
    (option) => option.value === filters.school
  );
  return {
    ...filters,
    district: districtId,
    school: schoolStillAvailable ? filters.school : "",
  };
}

function matchesSearch(row: AdminAccountRow, query: string): boolean {
  if (!query) return true;
  return (
    row.email.toLowerCase().includes(query) ||
    row.userName.toLowerCase().includes(query) ||
    (row.districtName?.toLowerCase().includes(query) ?? false) ||
    (row.schoolName?.toLowerCase().includes(query) ?? false)
  );
}

export function filterAccounts(
  accounts: AdminAccountRow[],
  filters: AccountFilters,
  search: string
): AdminAccountRow[] {
  const query = search.trim().toLowerCase();
  return accounts.filter((row) => {
    if (!matchesSearch(row, query)) return false;
    if (filters.status && statusFilterValue(row) !== filters.status) return false;
    if (filters.district && row.districtId !== filters.district) return false;
    if (filters.school && row.schoolId !== filters.school) return false;
    if (filters.orgRole) {
      const matchesRole =
        filters.orgRole === NO_ORG_ROLE_VALUE
          ? row.roles.length === 0
          : row.roles.includes(filters.orgRole);
      if (!matchesRole) return false;
    }
    if (filters.admin && (filters.admin === "yes") !== row.isSuperAdmin) return false;
    return true;
  });
}

export type AccountSortColumn =
  | "name"
  | "email"
  | "org"
  | "orgRole"
  | "status"
  | "lastLogin"
  | "admin";

export type SortDirection = "asc" | "desc";

export interface AccountSort {
  column: AccountSortColumn;
  direction: SortDirection;
}

export const ACCOUNT_SORT_LABELS: Record<AccountSortColumn, string> = {
  name: "Name",
  email: "Email",
  org: "Org",
  orgRole: "Org role",
  status: "Status",
  lastLogin: "Last login",
  admin: "Admin",
};

/**
 * Columns ordered by time rather than by text. They still compare as strings —
 * ISO 8601 sorts lexicographically in chronological order — but the direction has
 * to be described as oldest/newest, because "A–Z" says nothing to someone reading
 * a date column.
 */
const CHRONOLOGICAL_COLUMNS = new Set<AccountSortColumn>(["lastLogin"]);

/** Tri-state cycle: unsorted -> ascending -> descending -> unsorted. */
export function nextSortState(
  current: AccountSort | null,
  column: AccountSortColumn
): AccountSort | null {
  if (!current || current.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return null;
}

/** Sorts on the exact string the cell renders, so the order matches what the admin reads. */
export function accountSortValue(row: AdminAccountRow, column: AccountSortColumn): string {
  switch (column) {
    case "name":
      return row.userName;
    case "email":
      return row.email;
    case "org":
      return [row.districtName, row.schoolName].filter(Boolean).join(" · ");
    case "orgRole":
      return row.roles.length === 0 ? "" : formatAppRoles(row.roles);
    case "status":
      return isAccountPaused(row.userStatus) ? "Paused" : row.userStatus;
    case "lastLogin":
      // The raw timestamp, not the formatted cell: "Aug 17, 2026" would sort before
      // "Feb 3, 2026". ISO 8601 from PostgREST is fixed-width and UTC, so comparing
      // it as a string is already chronological. An account that never signed in
      // returns "" and lands in the blanks bucket, which trails in both directions —
      // the right place for missing data, unlike Admin's two-valued "Yes"/"No".
      return row.lastSignInAt ?? "";
    case "admin":
      // "No" rather than "" even though the cell renders "—": Admin is a two-valued
      // column where the dash means No, not missing data. Returning "" would put
      // every non-admin in the blanks bucket, which trails in *both* directions —
      // so the second click on the header would not appear to do anything.
      return row.isSuperAdmin ? "Yes" : "No";
  }
}

export function sortAccounts(
  accounts: AdminAccountRow[],
  sort: AccountSort | null
): AdminAccountRow[] {
  if (!sort) return accounts;
  const factor = sort.direction === "asc" ? 1 : -1;
  return [...accounts].sort((a, b) => {
    const left = accountSortValue(a, sort.column);
    const right = accountSortValue(b, sort.column);
    // Cells rendered as "—" always trail, so flipping direction never buries the real values.
    if (!left && !right) return 0;
    if (!left) return 1;
    if (!right) return -1;
    return factor * left.localeCompare(right, "en", { sensitivity: "base" });
  });
}

export function describeSort(sort: AccountSort | null): string | null {
  if (!sort) return null;
  const direction = CHRONOLOGICAL_COLUMNS.has(sort.column)
    ? sort.direction === "asc"
      ? "oldest first"
      : "newest first"
    : sort.direction === "asc"
      ? "A–Z"
      : "Z–A";
  return `${ACCOUNT_SORT_LABELS[sort.column]} (${direction})`;
}

export interface ActiveFilterChip {
  key: AccountFilterKey;
  label: string;
  value: string;
}

export function describeActiveFilters(
  filters: AccountFilters,
  options: AccountFilterOptions
): ActiveFilterChip[] {
  return (Object.keys(ACCOUNT_FILTER_LABELS) as AccountFilterKey[])
    .filter((key) => filters[key] !== "")
    .map((key) => ({
      key,
      label: ACCOUNT_FILTER_LABELS[key],
      value: options[key].find((option) => option.value === filters[key])?.label ?? filters[key],
    }));
}
